import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { agrogestao } from '@/api/agrogestaoClient';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { MapPin, Search, Download, Eye, TicketPercent, Loader2, Building2 } from 'lucide-react';

export function PropriedadesAdmin({ setTab, setSelectedPropId, refresh }) {
    const [busca, setBusca] = useState('');
    const [plano, setPlano] = useState('todos');
    const [status, setStatus] = useState('todos');
    const [periodoTipo, setPeriodoTipo] = useState('cadastro');
    const [inicio, setInicio] = useState('');
    const [fim, setFim] = useState('');
    const [page, setPage] = useState(1);
    const pageSize = 20;

    const [detalheOpen, setDetalheOpen] = useState(false);
    const [propSel, setPropSel] = useState(null);

    const { data: propriedades = [], isLoading } = useQuery({
        queryKey: ['propriedades_admin', refresh],
        queryFn: async () => {
            const { data, error } = await agrogestao.client
                .from('propriedades')
                .select('id, nome, area_total, localizacao, car, tipo_atividade, created_by, created_date')
                .order('created_date', { ascending: false })
                .limit(5000);
            if (error) return [];
            return data || [];
        }
    });

    const propIds = useMemo(() => propriedades.map(p => p.id).filter(Boolean), [propriedades]);

    const { data: licencas = [] } = useQuery({
        queryKey: ['licencas_props_admin_all', refresh],
        queryFn: async () => {
            const { data, error } = await agrogestao.client
                .from('licencas')
                .select('*')
                .order('valid_until', { ascending: false })
                .limit(5000);
            if (error) return [];
            return data || [];
        }
    });

    const { data: produtores = [] } = useQuery({
        queryKey: ['users_admin_props_all'],
        queryFn: async () => {
            const { data, error } = await agrogestao.client.from('users').select('email, full_name').limit(1000);
            if (error) return [];
            return data || [];
        }
    });

    const usersByEmail = useMemo(() => {
        const map = {};
        produtores.forEach(u => { map[String(u.email)] = u; });
        return map;
    }, [produtores]);

    const licsByProp = useMemo(() => {
        const map = {};
        (licencas || []).forEach(l => {
            const k = String(l.propriedade_id || '');
            if (!map[k]) map[k] = [];
            map[k].push(l);
        });
        return map;
    }, [licencas]);

    const filtered = useMemo(() => {
        const hoje = new Date();
        return (propriedades || []).filter(p => {
            const lics = licsByProp[String(p.id || '')] || [];
            const ativa = lics.find(l => {
                const exp = l.valid_until ? new Date(l.valid_until) : null;
                return l.status === 'ativo' && (exp ? exp >= hoje : true);
            });
            const planoAtivo = ativa ? ativa.plano : 'sem_plano';

            if (plano !== 'todos' && planoAtivo !== plano) return false;

            if (status !== 'todos') {
                if (status === 'ativo' && !ativa) return false;
                if (status === 'sem_plano' && (lics.length > 0)) return false;
            }

            if (busca) {
                const b = busca.toLowerCase();
                const prod = usersByEmail[String(p.created_by || '')];
                const campos = [
                    String(p.nome || ''),
                    String(p.localizacao || ''),
                    String(prod?.full_name || ''),
                    String(prod?.email || '')
                ].map(x => x.toLowerCase());
                if (!campos.some(c => c.includes(b))) return false;
            }

            if (inicio || fim) {
                let d = periodoTipo === 'cadastro' ? (p.created_date ? new Date(p.created_date) : null) : null;
                if (inicio && d && d < new Date(inicio)) return false;
                if (fim && d && d > new Date(fim)) return false;
            }

            return true;
        });
    }, [propriedades, licsByProp, usersByEmail, plano, status, busca, inicio, fim, periodoTipo]);

    const pageData = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filtered.slice(start, start + pageSize);
    }, [filtered, page]);
    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

    const exportCsv = () => {
        const header = ['Nome', 'Localizacao', 'Area', 'Produtor', 'Plano', 'Status', 'Cadastro'];
        const rows = filtered.map(p => {
            const lics = licsByProp[String(p.id || '')] || [];
            const ativa = lics.find(l => l.status === 'ativo');
            const prod = usersByEmail[String(p.created_by || '')];
            return [
                p.nome || '-',
                p.localizacao || '-',
                p.area_total || '0',
                prod?.full_name || p.created_by || '-',
                ativa?.plano || 'Sem plano',
                ativa?.status || 'Inativo',
                p.created_date ? format(new Date(p.created_date), 'dd/MM/yyyy') : '-'
            ];
        });
        const csv = [header.join(','), ...rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `propriedades_admin_${format(new Date(), 'yyyy-MM-dd')}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const exportPdf = () => {
        const w = window.open('', '_blank');
        if (!w) return;
        const rows = filtered.map(p => {
            const lics = licsByProp[String(p.id || '')] || [];
            const ativa = lics.find(l => l.status === 'ativo');
            const prod = usersByEmail[String(p.created_by || '')];
            return `<tr>
                <td>${p.nome || '-'}</td>
                <td>${p.localizacao || '-'}</td>
                <td>${p.area_total || '0'}</td>
                <td>${prod?.full_name || p.created_by || '-'}</td>
                <td>${ativa?.plano || 'Sem plano'}</td>
                <td>${ativa?.status || 'Inativo'}</td>
                <td>${p.created_date ? format(new Date(p.created_date), 'dd/MM/yyyy') : '-'}</td>
            </tr>`;
        }).join('');
        w.document.write(`
            <html>
                <head><title>Relatório de Propriedades</title><style>table{width:100%;border-collapse:collapse;}th,td{border:1px solid #ccc;padding:8px;text-align:left;}th{background:#f4f4f4;}</style></head>
                <body>
                    <h2>Gestão de Propriedades - AgroGestão</h2>
                    <p>Total: ${filtered.length} propriedades</p>
                    <table>
                        <thead><tr><th>Nome</th><th>Cidade</th><th>Área</th><th>Produtor</th><th>Plano</th><th>Status</th><th>Cadastro</th></tr></thead>
                        <tbody>${rows}</tbody>
                    </table>
                </body>
            </html>
        `);
        w.document.close();
        w.print();
    };

    const abrirDetalhes = (p) => {
        setPropSel(p);
        setDetalheOpen(true);
    };

    const gerenciarLicencas = (propId) => {
        setSelectedPropId(String(propId));
        setTab('licencas');
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3 bg-stone-50 p-4 rounded-xl border border-stone-200 shadow-sm">
                <div className="flex items-center gap-2 flex-1 min-w-[250px] bg-white rounded-lg border px-3 py-1 shadow-inner focus-within:ring-2 focus-within:ring-indigo-500/20 transition-all">
                    <Search className="w-4 h-4 text-stone-400" />
                    <Input
                        placeholder="Nome, localização ou produtor..."
                        value={busca}
                        onChange={(e) => setBusca(e.target.value)}
                        className="border-none shadow-none focus-visible:ring-0 h-9"
                    />
                </div>

                <div className="flex items-center gap-2">
                    <Select value={plano} onValueChange={setPlano}>
                        <SelectTrigger className="w-40 h-9"><SelectValue placeholder="Plano" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="todos">Todos Planos</SelectItem>
                            <SelectItem value="trial">Trial</SelectItem>
                            <SelectItem value="mensal">Mensal</SelectItem>
                            <SelectItem value="anual">Anual</SelectItem>
                            <SelectItem value="sem_plano">Sem Plano</SelectItem>
                        </SelectContent>
                    </Select>

                    <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger className="w-40 h-9"><SelectValue placeholder="Status" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="todos">Todos Status</SelectItem>
                            <SelectItem value="ativo">Ativo</SelectItem>
                            <SelectItem value="sem_plano">Sem Licença</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex items-center gap-2 bg-white rounded-lg border p-1 shadow-inner">
                    <Select value={periodoTipo} onValueChange={setPeriodoTipo}>
                        <SelectTrigger className="w-36 h-7 border-none shadow-none text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="cadastro">Cadastrado em</SelectItem>
                        </SelectContent>
                    </Select>
                    <Input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} className="w-32 h-7 border-none py-0 text-xs" />
                    <span className="text-stone-300">|</span>
                    <Input type="date" value={fim} onChange={(e) => setFim(e.target.value)} className="w-32 h-7 border-none py-0 text-xs" />
                </div>

                <div className="flex gap-2 ml-auto">
                    <Button onClick={exportCsv} variant="outline" size="sm" className="h-9 gap-2">
                        <Download className="w-4 h-4" /> CSV
                    </Button>
                    <Button onClick={exportPdf} variant="outline" size="sm" className="h-9 gap-2">
                        <Download className="w-4 h-4" /> PDF
                    </Button>
                </div>
            </div>

            <Card className="overflow-hidden border-stone-200">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-stone-50 text-stone-600 border-b">
                            <tr>
                                <th className="text-left py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Propriedade</th>
                                <th className="text-left py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Cidadde</th>
                                <th className="text-left py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Área (ha)</th>
                                <th className="text-left py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Produtor</th>
                                <th className="text-left py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Plano Atual</th>
                                <th className="text-left py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Cadastro</th>
                                <th className="text-right py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                            {isLoading ? (
                                <tr><td colSpan={7} className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-500" /></td></tr>
                            ) : pageData.length === 0 ? (
                                <tr><td colSpan={7} className="py-20 text-center text-stone-500">Nenhuma propriedade encontrada</td></tr>
                            ) : pageData.map((p) => {
                                const u = usersByEmail[String(p.created_by || '')];
                                const lics = licsByProp[String(p.id || '')] || [];
                                const ativa = lics.find(l => {
                                    const exp = l.valid_until ? new Date(l.valid_until) : null;
                                    return l.status === 'ativo' && (exp ? exp >= new Date() : true);
                                });
                                return (
                                    <tr key={p.id} className="hover:bg-stone-50/50 transition-colors">
                                        <td className="py-4 px-4">
                                            <div className="font-semibold text-stone-900">{p.nome || '-'}</div>
                                            <div className="text-[10px] text-stone-400 font-mono">{p.id}</div>
                                        </td>
                                        <td className="py-4 px-4 text-stone-600">
                                            <div className="flex items-center gap-1.5 pt-2">
                                                <MapPin className="w-3 h-3 text-stone-400" /> {p.localizacao || '-'}
                                            </div>
                                        </td>
                                        <td className="py-4 px-4 text-stone-600 font-medium">{p.area_total ? `${Number(p.area_total).toLocaleString('pt-BR')} ha` : '-'}</td>
                                        <td className="py-4 px-4">
                                            <div className="flex flex-col">
                                                <span className="text-stone-900 font-medium">{u?.full_name || '-'}</span>
                                                <span className="text-xs text-stone-400">{u?.email || p.created_by}</span>
                                            </div>
                                        </td>
                                        <td className="py-4 px-4">
                                            {ativa ? (
                                                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border-none px-2 py-0.5 capitalize shadow-sm">
                                                    {ativa.plano}
                                                </Badge>
                                            ) : (
                                                <Badge variant="outline" className="text-stone-400 border-stone-200 bg-stone-50">Inativo</Badge>
                                            )}
                                        </td>
                                        <td className="py-4 px-4 text-stone-500 text-xs">{p.created_date ? format(new Date(p.created_date), 'dd/MM/yyyy') : '-'}</td>
                                        <td className="py-4 px-4 text-right">
                                            <div className="flex justify-end gap-1">
                                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-indigo-50 hover:text-indigo-600" onClick={() => abrirDetalhes(p)}>
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-emerald-50 hover:text-emerald-600" onClick={() => gerenciarLicencas(p.id)}>
                                                    <TicketPercent className="w-4 h-4" />
                                                </Button>
                                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-stone-100" onClick={() => window.open(`https://agrogestao.com/app?propriedade=${p.id}`, '_blank')}>
                                                    <span className="text-base">↗</span>
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Card>

            <div className="flex items-center justify-between px-2 pt-2">
                <div className="text-xs text-stone-400 font-medium">Mostrando {pageData.length} de {filtered.length} propriedades</div>
                <div className="flex items-center gap-3">
                    <Button variant="outline" size="sm" className="h-8 px-4" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>Anterior</Button>
                    <span className="text-xs font-semibold text-stone-600">Página {page} / {totalPages}</span>
                    <Button variant="outline" size="sm" className="h-8 px-4" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Próxima</Button>
                </div>
            </div>

            <Dialog open={detalheOpen} onOpenChange={setDetalheOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0 border-none bg-stone-50 shadow-2xl">
                    <div className="bg-indigo-600 p-6 text-white rounded-t-lg">
                        <DialogHeader>
                            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                                <Building2 className="w-6 h-6" /> {propSel?.nome}
                            </DialogTitle>
                            <DialogDescription className="text-indigo-100 opacity-90">
                                Gestão detalhada da propriedade e histórico de licenças
                              </DialogDescription>
                        </DialogHeader>
                    </div>

                    {propSel && (
                        <div className="p-6 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <Card className="border-none shadow-sm h-full">
                                    <CardHeader className="pb-2"><CardTitle className="text-xs uppercase text-stone-400">Informações Básicas</CardTitle></CardHeader>
                                    <CardContent className="space-y-3">
                                        <div>
                                            <Label className="text-[10px] text-stone-400 uppercase">Localização</Label>
                                            <div className="text-sm font-medium text-stone-900">{propSel.localizacao || '-'}</div>
                                        </div>
                                        <div>
                                            <Label className="text-[10px] text-stone-400 uppercase">Área Total</Label>
                                            <div className="text-sm font-medium text-stone-900">{propSel.area_total} ha</div>
                                        </div>
                                        <div>
                                            <Label className="text-[10px] text-stone-400 uppercase">CAR</Label>
                                            <div className="text-sm font-medium text-stone-900">{propSel.car || '-'}</div>
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card className="border-none shadow-sm h-full">
                                    <CardHeader className="pb-2"><CardTitle className="text-xs uppercase text-stone-400">Produtor Responsável</CardTitle></CardHeader>
                                    <CardContent className="space-y-3 font-medium">
                                        {(() => {
                                            const u = usersByEmail[propSel.created_by];
                                            return (
                                                <>
                                                    <div>
                                                        <Label className="text-[10px] text-stone-400 uppercase">Nome</Label>
                                                        <div className="text-sm text-stone-900">{u?.full_name || '-'}</div>
                                                    </div>
                                                    <div>
                                                        <Label className="text-[10px] text-stone-400 uppercase">Email</Label>
                                                        <div className="text-sm text-stone-900">{u?.email || propSel.created_by}</div>
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </CardContent>
                                </Card>

                                <Card className="border-none shadow-sm h-full bg-indigo-50/50">
                                    <CardHeader className="pb-2"><CardTitle className="text-xs uppercase text-indigo-400">Atividade Principal</CardTitle></CardHeader>
                                    <CardContent>
                                        <div className="text-sm font-bold text-indigo-700 capitalize">{propSel.tipo_atividade || '-'}</div>
                                    </CardContent>
                                </Card>
                            </div>

                            <Card className="border-none shadow-sm bg-white overflow-hidden">
                                <CardHeader className="bg-stone-50 border-b flex flex-row items-center justify-between py-3">
                                    <CardTitle className="text-sm font-bold text-stone-700 flex items-center gap-2">
                                        <TicketPercent className="w-4 h-4" /> Histórico de Licenças
                                    </CardTitle>
                                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8" onClick={() => gerenciarLicencas(propSel.id)}>
                                        Nova Licença / Ajustar
                                    </Button>
                                </CardHeader>
                                <CardContent className="p-0">
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead className="bg-stone-50/50 text-stone-400 border-b">
                                                <tr>
                                                    <th className="text-left py-2 px-4 uppercase font-bold text-[9px]">ID</th>
                                                    <th className="text-left py-2 px-4 uppercase font-bold text-[9px]">Plano</th>
                                                    <th className="text-left py-2 px-4 uppercase font-bold text-[9px]">Status</th>
                                                    <th className="text-left py-2 px-4 uppercase font-bold text-[9px]">Vencimento</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-stone-50">
                                                {(() => {
                                                    const list = licsByProp[String(propSel.id || '')] || [];
                                                    if (list.length === 0) return <tr><td colSpan={4} className="py-8 text-center text-stone-400">Nenhuma licença vinculada</td></tr>;
                                                    return list.map(l => (
                                                        <tr key={l.id} className="hover:bg-stone-50/30">
                                                            <td className="py-2.5 px-4 font-mono text-[10px]">{l.id}</td>
                                                            <td className="py-2.5 px-4 font-semibold capitalize">{l.plano}</td>
                                                            <td className="py-2.5 px-4">
                                                                <Badge variant="outline" className={`border-none px-2 py-0 text-[10px] ${l.status === 'ativo' ? 'bg-green-50 text-green-700' : 'bg-stone-100 text-stone-500'}`}>
                                                                    {l.status}
                                                                </Badge>
                                                            </td>
                                                            <td className="py-2.5 px-4 font-medium">{l.valid_until ? format(new Date(l.valid_until), 'dd/MM/yyyy') : '-'}</td>
                                                        </tr>
                                                    ));
                                                })()}
                                            </tbody>
                                        </table>
                                    </div>
                                </CardContent>
                            </Card>

                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <Button variant="outline" onClick={() => setDetalheOpen(false)}>Fechar</Button>
                                <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => window.open(`https://agrogestao.com/app?propriedade=${propSel.id}`, '_blank')}>Acessar Painel ↗</Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
